<template>
  <div class="project-page">
    <!-- Loader overlay -->
    <v-overlay :value="!isLoaded">
      <v-progress-circular
        indeterminate
        size="64"
      />
    </v-overlay>

    <template v-if="isLoaded">
      <v-container>
        <project-form
          :project.sync="localProject"
          :patient="patient"
          @on-submit="update"
          @on-cancel="dialog = true"
          @on-product-click="goToProduct"
          @on-transfer-click="syncAndSetDelivered"
          @on-delete-click="deleteProject"
          @media-click="$router.push({ name: 'MediasPage' })"
        />

        <v-snackbar
          v-model="showSnackbar"
          color="success"
          :timeout="2000"
          top
        >
          {{ textSnackbar }}
          <v-btn
            text
            @click="showSnackbar = false"
          >
            Fermer
          </v-btn>
        </v-snackbar>

        <!-- Confirmation dialog -->
        <v-dialog
          v-model="dialog"
          max-width="290"
        >
          <v-card>
            <v-card-title class="headline">
              Annulation
            </v-card-title>
            <v-card-text>Toutes modifications seront perdues !</v-card-text>
            <v-card-actions>
              <div class="flex-grow-1" />
              <v-btn
                color="red"
                :to="{ name: 'projects-list-local' }"
                dark
                text
              >
                Oui
              </v-btn>
              <v-btn
                text
                color="black"
                @click="dialog = false"
              >
                Non
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-container>
    </template>
  </div>
</template>

<script>
import ErrorHandlingMixin from "@/mixins/ErrorHandlingMixin";
import { productsObject } from "@/utils";
import { mapActions, mapMutations, mapState } from "vuex";

// Components
import ProjectForm from "./components/ProjectForm";

// Mixins
import { synchronization } from "@/mixins/synchronization";

// Repos
import ProjectRepo from "@/repositories/projectsRepository.js";

export default {
  name: "ProjectPage",
  components: {
    ProjectForm
  },
  mixins: [synchronization, ErrorHandlingMixin],
  data() {
    return {
      isLoaded: false,
      ready: false,
      localProject: null,
      patient: null,

      showSnackbar: false,
      textSnackbar: "",
      dialog: false
    };
  },
  computed: {
    ...mapState({
      project: state => state.projects.project
    })
  },

  mounted() {
    this.loadProject();
  },

  beforeDestroy() {
    this.resetProject();
  },

  methods: {
    ...mapMutations({
      setProject: "projects/setProject"
    }),

    ...mapActions({
      resetProject: "projects/resetProject",
      getLocalProject: "projects/getLocalProject"
    }),

    // Load project
    async loadProject() {
      try {
        // Clean project state
        this.resetProject();

        // Get project
        const uuid = this.$route.params.uiud || this.$route.params.projectUuid;
        const project = await this.getLocalProject(uuid);

        // Set project state
        this.setProject(project);

        // Deep clone
        this.localProject = JSON.parse(JSON.stringify(this.project));

        this.patient = await this.loadPatient(this.localProject.patient.uuid);

        this.isLoaded = true;
      } catch (error) {
        this.handleError(error);
      }
    },

    // Load patient
    async loadPatient(uuid) {
      try {
        return await this.$db.patients.get(uuid);
      } catch (error) {
        this.handleError(error);
      }
    },

    // Update local project
    async update() {
      try {
        // Project uuid
        const projectUuid = this.$route.params.projectUuid;

        // Update project
        await this.$db.projects.update(projectUuid, this.localProject);

        await this.$db.projects.update(projectUuid, { updated: 1 });
        const project = await this.$db.projects.get(projectUuid);

        // Check if patient was sent to server
        if (!project.server_version) {
          // Can update patient info
          await this.$db.patients.update(this.patient.uuid, this.patient);
        }

        // Delete useless properties
        delete project.local_version;
        delete project.server_version;
        delete project.updated;

        // Set new project
        this.setProject(project);

        this.$notify({
          group: "main",
          type: "success",
          title: "Succès",
          text: "Fiche enregistrée",
          duration: 5000
        });
      } catch (error) {
        this.handleError(error);
      }
    },

    // Open product based on the selected product
    goToProduct() {
      switch (this.localProject.product_model) {
        case "NECK_BRACE":
          this.$router.push({ name: "NeckBraceProduct" });
          break;
        case "MOLDED_SEAT":
          this.$router.push({ name: "MoldedSeatProduct" });
          break;
        case "OTN":
          this.$router.push({ name: "OtnProduct" });
          break;
        case "LIFT":
          this.$router.push({ name: "LiftProduct" });
          break;
        case "RETOUCHING_REPAIR":
          this.$router.push({ name: "RetouchingRepairProduct" });
          break;
        case "SEAT":
          this.$router.push({ name: "SeatProduct" });
          break;
        case "POSITIONING_SEAT":
          this.$router.push({ name: "PositioningSeatProduct" });
          break;
        case "UPPER_LIMB_ORTHOSIS":
          this.$router.push({ name: "UpperLimbProduct" });
          break;
        case "LOWER_LIMB_ORTHOSIS":
          this.$router.push({ name: "LowerLimbProduct" });
          break;
        case "CORSET":
          this.$router.push({ name: "CorsetProduct" });
          break;

        default:
          break;
      }
    },

    // Sync project and model and set project delivered
    async syncAndSetDelivered() {
      try {
        // this.localProject.idc_device_delivery = true;
        // const now = new Date().toISOString();
        // this.localProject.real_delivery_at = now.substring(0, now.indexOf("T"));

        if (!this.localProject.created_at) {
          await this.synchronize(this.localProject);
        }

        await ProjectRepo.setDelivery(this.localProject.uuid);

        await this.synchronize(this.localProject, true);

        this.loadProject();

        const notification = {
          message: `Projet synchronisé et livré!`,
          type: "success"
        };
        this.$store.dispatch("notifications/add", notification);
      } catch (error) {
        const notification = {
          message: error,
          type: "error"
        };
        this.$store.dispatch("notifications/add", notification);
      }
    },

    // Delete project from indexedDB
    async deleteProject() {
      const project = JSON.parse(JSON.stringify(this.localProject));
      console.log(project);
      try {
        const productTableName =
          productsObject[project.product_model].tableName;

        // Delete project
        await this.$db.projects.delete(project.uuid);

        // Delete patient
        await this.$db.patients.delete(project.patient.uuid);

        // Delete product
        await this.$db[productTableName].delete(project.product_uuid);

        // Get project medias from local db
        const localMedias = await this.$db.medias
          .where({ project_uuid: project.uuid })
          .toArray();

        // Create an array of medias keys
        if (localMedias) {
          let mediasKeysToDelete = [];
          for (let i = 0; i < localMedias.length; i++) {
            mediasKeysToDelete.push(localMedias[i].uuid);
          }

          // Delete medias
          await this.$db.medias.bulkDelete(mediasKeysToDelete);
        }

        this.$router.push({ name: "projects-list-local" });
      } catch (error) {
        throw error;
      }
    }
  }
};
</script>

<style lang="scss">
.project-page {
  height: 100%;
}
</style>
